import React, { useState, useEffect, useMemo } from "react";
import { Button, Table, Spinner, Modal, Form, Badge, Row, Col, Card } from "react-bootstrap";
import { useTable, usePagination, useGlobalFilter, useSortBy } from "react-table";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useDispatch } from "react-redux";
import { Fn_FillListData, Fn_DeleteData } from "../../store/Functions";
import { useNavigate } from "react-router-dom";

const PageList_MemberMaster = () => {
  const [gridData, setGridData] = useState([]);
  const [deleteLogs, setDeleteLogs] = useState([]);
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [viewModal, setViewModal] = useState({ show: false, member: null, walletInfo: null, loadingWallet: false });
  const [previewImage, setPreviewImage] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const API_URL = `${API_WEB_URLS.MASTER}/0/token/MemberMaster`;
  const API_DELETE_LOG_URL = `${API_WEB_URLS.MASTER}/0/token/DeleteLog`;
  const API_VEHICLE_TYPE_URL = `${API_WEB_URLS.MASTER}/0/token/VehicleType/Id/0`;

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
      Fn_FillListData(dispatch, setVehicleTypes, "gridData", API_VEHICLE_TYPE_URL);
      setLoading(false);
    };
    fetchData();
  }, [dispatch, API_URL, API_VEHICLE_TYPE_URL]);

  useEffect(() => {
    if (showDeleted && deleteLogs.length === 0) {
      Fn_FillListData(dispatch, setDeleteLogs, "gridData", API_DELETE_LOG_URL + "/Id/0");
    }
  }, [showDeleted, dispatch, API_DELETE_LOG_URL, deleteLogs.length]);

  const handleToggleShowDeleted = (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    const nextVal = !showDeleted;
    setShowDeleted(nextVal);
    if (nextVal && deleteLogs.length === 0) {
      Fn_FillListData(dispatch, setDeleteLogs, "gridData", API_DELETE_LOG_URL + "/Id/0");
    }
  };

  // Helper to resolve VehicleType ID (e.g. 4) to human-readable Name
  const getVehicleTypeName = (member) => {
    if (!member) return "-";
    if (member.VehicleTypeName && isNaN(member.VehicleTypeName)) {
      return member.VehicleTypeName;
    }
    const typeId = member.VehicleTypeName || member.F_VehicleType || member.VehicleType;
    if (typeId && vehicleTypes && vehicleTypes.length > 0) {
      const matched = vehicleTypes.find((v) => String(v.Id || v.id) === String(typeId));
      if (matched) return matched.Name || matched.name;
    }
    return typeId ? `Type #${typeId}` : "-";
  };

  // Handle viewing member details and fetching wallet/earnings info for captains
  const handleOpenViewModal = (member) => {
    setViewModal({ show: true, member, walletInfo: null, loadingWallet: true });
    const isCapt = member.F_UserTypeMaster === 2 || member.UserType === "Captain" || member.UserType === "Driver" || member.DrivingLicence;
    if (isCapt && !member.isDeleted) {
      const walletUrl = `${API_WEB_URLS.MASTER}/0/token/WalletBalance/Id/${member.Id}`;
      Fn_FillListData(
        dispatch,
        (res) => {
          const wInfo = Array.isArray(res) && res.length > 0 ? res[0] : null;
          setViewModal((prev) => ({ ...prev, walletInfo: wInfo, loadingWallet: false }));
        },
        "walletData",
        walletUrl
      );
    } else {
      setViewModal((prev) => ({ ...prev, loadingWallet: false }));
    }
  };

  // Combine active members & deleted members from audit log when showDeleted is checked
  const combinedData = useMemo(() => {
    let list = (gridData || []).map((m) => ({ ...m, isDeleted: false }));
    if (showDeleted && deleteLogs && deleteLogs.length > 0) {
      const deletedMembers = deleteLogs
        .filter((log) => log.TableName === "MemberMaster" && log.DeletedDataJson)
        .map((log) => {
          let parsedData = {};
          try {
            parsedData = JSON.parse(log.DeletedDataJson);
          } catch (e) {
            console.error("Error parsing DeleteLog JSON:", e);
          }
          return {
            ...parsedData,
            Id: log.RecordId || parsedData.Id,
            Name: parsedData.Name || `Deleted Member #${log.RecordId}`,
            MobileNo: parsedData.MobileNo || "-",
            Email: parsedData.Email || "-",
            UserType: parsedData.UserType || "Member",
            isDeleted: true,
            deletedOn: log.DeletedOn,
            deletedBy: log.DeletedBy || "Admin",
            logId: log.Id,
          };
        });
      list = [...list, ...deletedMembers];
    }
    // Ensure descending order by ID
    list.sort((a, b) => Number(b.Id || 0) - Number(a.Id || 0));
    return list;
  }, [gridData, deleteLogs, showDeleted]);

  const data = useMemo(() => combinedData, [combinedData]);

  const columns = useMemo(
    () => [
      {
        Header: "Status",
        accessor: "isDeleted",
        Cell: ({ row }) =>
          row.original.isDeleted ? (
            <Badge bg="danger" style={{ fontSize: 10 }}>DELETED LOG</Badge>
          ) : (
            <Badge bg="success" style={{ fontSize: 10 }}>ACTIVE</Badge>
          ),
      },
      { Header: "Name", accessor: "Name", Cell: ({ value, row }) => (
          <span style={{ fontSize: 12, fontWeight: 600, color: row.original.isDeleted ? "#DC2626" : "#111827" }}>
            {value || "-"}
          </span>
        )
      },
      { Header: "Mobile", accessor: "MobileNo", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
      { Header: "Email", accessor: "Email", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
      { Header: "City", accessor: "City", Cell: ({ value }) => <span style={{ fontSize: 11 }}>{value || "-"}</span> },
      { Header: "Type", accessor: "UserType", Cell: ({ value, row }) => {
          const typeVal = value || (row.original.F_UserTypeMaster === 2 ? "Captain" : "User");
          const isCapt = typeVal === "Captain" || typeVal === "Driver" || row.original.F_UserTypeMaster === 2;
          return (
            <Badge bg={isCapt ? "warning" : "info"} text={isCapt ? "dark" : "white"} style={{ fontSize: 10 }}>
              {isCapt ? "🚖 Captain" : "👤 Customer"}
            </Badge>
          );
        }
      },
      {
        id: "actions",
        Header: "Action",
        Cell: ({ row }) => {
          const member = row.original;
          return (
            <div style={{ display: "flex", gap: 4 }}>
              <Button
                size="sm"
                variant="outline-info"
                className="py-0 px-2"
                style={{ fontSize: 11, fontWeight: 600 }}
                onClick={() => handleOpenViewModal(member)}
              >
                👁️ View
              </Button>
              {!member.isDeleted && (
                <>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    className="py-0 px-1"
                    style={{ fontSize: 11 }}
                    onClick={() => navigate("/AddMemberMaster", { state: { Id: member.Id } })}
                  >
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    className="py-0 px-1"
                    style={{ fontSize: 11, fontWeight: 600 }}
                    onClick={() => {
                      const memberName = member.Name || "this member";
                      const confirmMsg = `⚠️ ARE YOU SURE YOU WANT TO PERMANENTLY DELETE MEMBER:\n"${memberName}" (ID: #${member.Id})?\n\nThis will permanently delete & log:\n• Member Master Profile\n• Wallet Ledger & Voucher Entries\n• Withdrawal Requests\n• Vehicle Registrations\n• Bookings & Trip History\n\nAre you sure you want to proceed?`;
                      if (window.confirm(confirmMsg)) {
                        Fn_DeleteData(dispatch, member.Id, API_URL, API_URL + "/Id/0").then(() => {
                          alert(`Member "${memberName}" and all associated data have been deleted and logged.`);
                          Fn_FillListData(dispatch, setGridData, "gridData", API_URL + "/Id/0");
                          if (showDeleted) {
                            Fn_FillListData(dispatch, setDeleteLogs, "gridData", API_DELETE_LOG_URL + "/Id/0");
                          }
                        });
                      }
                    }}
                  >
                    🗑️ Delete
                  </Button>
                </>
              )}
            </div>
          );
        },
      },
    ],
    [dispatch, navigate, API_URL, API_DELETE_LOG_URL, showDeleted]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    rows,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, globalFilter },
    setGlobalFilter,
  } = useTable(
    { columns, data, initialState: { pageIndex: 0, pageSize: 15 } },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  // ─── Format & Photo Helpers for Complete Export ──────────────────────────────
  const formatDate = (val) => {
    if (!val || val === "-" || val === "null" || val === "undefined") return "-";
    try {
      const d = new Date(val);
      if (isNaN(d.getTime())) return String(val).split("T")[0];
      return d.toISOString().split("T")[0];
    } catch (e) {
      return String(val);
    }
  };

  const getExpiryStatus = (val) => {
    if (!val || val === "-" || val === "null" || val === "undefined") return "Not Set";
    const dateStr = formatDate(val);
    if (dateStr === "-") return "Not Set";
    const expDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (!isNaN(expDate.getTime())) {
      if (expDate < today) {
        return "EXPIRED";
      } else {
        const diffDays = Math.ceil((expDate - today) / (1000 * 60 * 60 * 24));
        if (diffDays <= 30) {
          return `Expiring soon (${diffDays}d)`;
        }
        return "Valid";
      }
    }
    return "Valid";
  };

  const getFullPhotoUrl = (fileName) => {
    if (!fileName || fileName === "-" || fileName === "null" || fileName === "undefined") return "";
    const str = String(fileName).trim();
    if (!str) return "";
    if (str.startsWith("http://") || str.startsWith("https://")) return str;
    return `${API_WEB_URLS.IMAGEURL}${str}`;
  };

  // Maps single member to full 50+ column dictionary
  const mapMemberToExportRow = (m) => {
    const isCapt = m.F_UserTypeMaster === 2 || m.UserType === "Captain" || m.UserType === "Driver" || m.DrivingLicence;
    const userTypeStr = m.UserType || (isCapt ? "Captain / Driver" : "Customer / User");
    const memberCategoryStr = m.F_MemberCategory === 1 || String(m.F_MemberCategory) === "1" ? "Corporate / Company" : "Individual";

    const dlExp = formatDate(m.DLExpiryDate);
    const insExp = formatDate(m.InsuranceExpiryDate);
    const pucExp = formatDate(m.PUCExpiryDate);

    const dlStatus = getExpiryStatus(m.DLExpiryDate);
    const insStatus = getExpiryStatus(m.InsuranceExpiryDate);
    const pucStatus = getExpiryStatus(m.PUCExpiryDate);

    return {
      "Member ID": m.Id || "-",
      "Status": m.isDeleted ? "DELETED LOG" : "ACTIVE",
      "User Type": userTypeStr,
      "Member Category": memberCategoryStr,
      "Full Name": m.Name || "-",
      "Father Name": m.FatherName || "-",
      "Mother Name": m.MotherName || "-",
      "Date of Birth": formatDate(m.DateOfBirth),
      "Age": m.Age || "-",
      "Mobile No": m.MobileNo || "-",
      "Alternate Mobile No": m.AlternateMobileNo || "-",
      "Email": m.Email || "-",
      "Username": m.UserName || m.MobileNo || "-",
      "Full Address": m.Address || "-",
      "City": m.CityName || m.City || "-",
      "State": m.StateName || "-",
      "Country": m.CountryName || "India",
      "Pincode": m.Pincode || "-",
      "Aadhaar Number": m.AadharNo || "-",
      "PAN Number": m.PANNo || m.PanNo || "-",
      "Driving Licence No": m.DrivingLicence || "-",
      "Company Name": m.CompanyName || "-",
      "GST Number": m.GSTNo || "-",
      "Vehicle Type": getVehicleTypeName(m),
      "Vehicle Name": m.VehicleName || "-",
      "Vehicle Registration No": m.VehicleRegistrationNo || "-",
      "Vehicle Plate No": m.VehicleNo || "-",
      "Vehicle Body Type": m.VehicleBodyType || "-",
      "Weight Capacity": m.WeightCapacity ? `${m.WeightCapacity} KG` : "-",
      "Bank Name": m.BankName || "-",
      "Bank Account Number": m.AccountNo || "-",
      "Account Holder Name": m.AccountPersonName || m.Name || "-",
      "IFSC Code": m.IFSC || "-",
      "DL Expiry Date": dlExp,
      "DL Expiry Status": dlStatus,
      "Insurance Expiry Date": insExp,
      "Insurance Expiry Status": insStatus,
      "PUC Expiry Date": pucExp,
      "PUC Expiry Status": pucStatus,
      "Profile Photo URL": getFullPhotoUrl(m.UserPhoto || m.ImageURL_Member),
      "Aadhaar Front URL": getFullPhotoUrl(m.AadharFront),
      "Aadhaar Back URL": getFullPhotoUrl(m.AadharBack),
      "RC Front URL": getFullPhotoUrl(m.RCFront || m.RcFront),
      "RC Back URL": getFullPhotoUrl(m.RCBack || m.RcBack),
      "DL Front URL": getFullPhotoUrl(m.DrivingLicenseFront),
      "DL Back URL": getFullPhotoUrl(m.DrivingLicenseBack),
      "Insurance Doc URL": getFullPhotoUrl(m.Insurance),
      "PUC Certificate URL": getFullPhotoUrl(m.PUC),
      "PAN Card URL": getFullPhotoUrl(m.PAN || m.PanPhoto),
      "Is Approved": m.IsApproved === 1 || m.IsApproved === true ? "Approved" : "Pending",
      "Is Active": m.IsActive !== false && m.IsActive !== 0 ? "Active" : "Inactive",
      "Deleted On": m.deletedOn || "-",
      "Deleted By": m.deletedBy || "-",
    };
  };

  // ─── Export to Excel (.xls) with Clickable Photos and Styled Header ─────────
  const handleExportFullExcel = () => {
    const exportDataList = (rows && rows.length > 0 ? rows.map((r) => r.original) : data) || [];
    if (exportDataList.length === 0) {
      alert("No data available to export.");
      return;
    }

    const mappedRows = exportDataList.map(mapMemberToExportRow);
    const columns = Object.keys(mappedRows[0]);

    let tableHtml = `<table border="1">`;
    // Header Row
    tableHtml += `<tr style="background-color: #E87B2D; color: #FFFFFF; font-weight: bold; height: 32px;">`;
    tableHtml += `<th style="background-color: #E87B2D; color: #FFFFFF; padding: 6px 10px; border: 1px solid #B05510;">#</th>`;
    columns.forEach((col) => {
      tableHtml += `<th style="background-color: #E87B2D; color: #FFFFFF; padding: 6px 10px; border: 1px solid #B05510;">${col}</th>`;
    });
    tableHtml += `</tr>`;

    // Body Rows
    mappedRows.forEach((row, idx) => {
      const isEven = idx % 2 === 0;
      const isDel = row["Status"] === "DELETED LOG";
      const bgColor = isDel ? "#FEE2E2" : isEven ? "#FFFFFF" : "#F9FAFB";

      tableHtml += `<tr style="background-color: ${bgColor}; height: 26px;">`;
      tableHtml += `<td style="padding: 4px 8px; text-align: center; border: 1px solid #E5E7EB;">${idx + 1}</td>`;

      columns.forEach((col) => {
        let val = row[col];
        if (val === null || val === undefined) val = "-";

        let cellContent = String(val);

        if (col.includes("URL") && val && String(val).startsWith("http")) {
          cellContent = `<a href="${val}" target="_blank" style="color: #2563EB; font-weight: 600; text-decoration: underline;">Open Photo</a>`;
        } else if (col.includes("Status") && String(val) === "EXPIRED") {
          cellContent = `<span style="color: #DC2626; font-weight: bold;">EXPIRED</span>`;
        } else if (col.includes("Status") && String(val) === "Valid") {
          cellContent = `<span style="color: #16A34A; font-weight: 600;">Valid</span>`;
        } else if (col === "Status" && isDel) {
          cellContent = `<span style="color: #DC2626; font-weight: bold;">DELETED LOG</span>`;
        }

        tableHtml += `<td style="padding: 4px 8px; border: 1px solid #E5E7EB;">${cellContent}</td>`;
      });
      tableHtml += `</tr>`;
    });
    tableHtml += `</table>`;

    const fullExcelDocument = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
        <head>
          <meta http-equiv="content-type" content="application/vnd.ms-excel; charset=UTF-8">
          <!--[if gte mso 9]>
          <xml>
            <x:ExcelWorkbook>
              <x:ExcelWorksheets>
                <x:ExcelWorksheet>
                  <x:Name>Member Master</x:Name>
                  <x:WorksheetOptions>
                    <x:DisplayGridlines/>
                  </x:WorksheetOptions>
                </x:ExcelWorksheet>
              </x:ExcelWorksheets>
            </x:ExcelWorkbook>
          </xml>
          <![endif]-->
          <style>
            body { font-family: Calibri, Arial, sans-serif; font-size: 11pt; }
            table { border-collapse: collapse; width: 100%; }
            th { font-family: Calibri, Arial, sans-serif; font-size: 11pt; }
            td { font-family: Calibri, Arial, sans-serif; font-size: 10.5pt; }
          </style>
        </head>
        <body>
          <div style="font-size: 16pt; font-weight: bold; color: #E87B2D; margin-bottom: 4px;">KIRAYAGO LOGISTICS - MEMBER MASTER COMPLETE REPORT</div>
          <div style="font-size: 10pt; color: #6B7280; margin-bottom: 12px;">Generated On: ${new Date().toLocaleString("en-IN")} | Total Records: ${mappedRows.length}</div>
          ${tableHtml}
        </body>
      </html>
    `;

    const blob = new Blob([fullExcelDocument], { type: "application/vnd.ms-excel;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Kirayago_Member_Master_Full_${new Date().toISOString().split("T")[0]}.xls`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── Export to CSV with All Fields ──────────────────────────────────────────
  const handleExportFullCSV = () => {
    const exportDataList = (rows && rows.length > 0 ? rows.map((r) => r.original) : data) || [];
    if (exportDataList.length === 0) {
      alert("No data available to export.");
      return;
    }

    const mappedRows = exportDataList.map(mapMemberToExportRow);
    const columns = Object.keys(mappedRows[0]);

    let csvContent = "";
    csvContent += `"KIRAYAGO LOGISTICS - MEMBER MASTER COMPLETE EXPORT"\n`;
    csvContent += `"Generated On","${new Date().toLocaleString("en-IN")}","Total Records","${mappedRows.length}"\n\n`;

    // Headers
    csvContent += columns.map((c) => `"${c}"`).join(",") + "\n";

    // Rows
    mappedRows.forEach((row) => {
      const rowValues = columns.map((col) => {
        let val = row[col];
        if (val === null || val === undefined) val = "-";
        return `"${String(val).replace(/"/g, '""')}"`;
      });
      csvContent += rowValues.join(",") + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `Kirayago_Member_Master_Full_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading)
    return (
      <div style={{ padding: "6px 8px", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 120 }}>
        <Spinner animation="border" size="sm" />
      </div>
    );

  const selected = viewModal.member;
  const walletInfo = viewModal.walletInfo;
  const isCaptain = selected && (selected.F_UserTypeMaster === 2 || selected.UserType === "Captain" || selected.UserType === "Driver" || selected.DrivingLicence || selected.AadharNo);

  const docList = selected ? [
    { title: "Profile Photo / Selfie", url: selected.UserPhoto },
    { title: "Aadhaar Front", url: selected.AadharFront },
    { title: "Aadhaar Back", url: selected.AadharBack },
    { title: "RC Front", url: selected.RcFront || selected.RCFront },
    { title: "RC Back", url: selected.RcBack || selected.RCBack },
    { title: "DL Front", url: selected.DrivingLicenseFront },
    { title: "DL Back", url: selected.DrivingLicenseBack },
    { title: "Insurance Document", url: selected.Insurance },
    { title: "PUC Document", url: selected.PUC },
    { title: "PAN Photo", url: selected.PAN || selected.PanPhoto },
  ].filter(d => d.url) : [];

  return (
    <div className="page-content" style={{ width: "100%", padding: "16px 20px" }}>
      <div className="container-fluid p-0" style={{ width: "100%", maxWidth: "100%" }}>
        <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10 }}>
          <Card.Body className="p-3">
            {/* Header Bar */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12, paddingBottom: 10, borderBottom: "2px solid #E87B2D", flexWrap: "wrap", gap: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ fontSize: 17, fontWeight: 700, color: "#1F2937" }}>👥 Member Master</span>
                <span className="badge bg-light text-dark fw-bold" style={{ fontSize: 12 }}>
                  Total: {data.length}
                </span>
                <div
                  className={`d-flex align-items-center gap-2 px-2 py-1 rounded border ${
                    showDeleted
                      ? "bg-danger text-white border-danger shadow-sm"
                      : "bg-light text-secondary border-secondary-subtle"
                  }`}
                  style={{ cursor: "pointer", userSelect: "none", transition: "all 0.2s ease" }}
                  onClick={handleToggleShowDeleted}
                  title="Click to toggle deleted members audit log records"
                >
                  <input
                    type="checkbox"
                    className="form-check-input m-0"
                    id="show-deleted-chk"
                    checked={showDeleted}
                    onChange={handleToggleShowDeleted}
                    style={{ cursor: "pointer", pointerEvents: "none" }}
                  />
                  <span style={{ fontSize: 12, fontWeight: 700 }}>
                    🗑️ Show Deleted Audit Logs {showDeleted ? "(ON)" : "(OFF)"}
                  </span>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <Button
                  size="sm"
                  variant="outline-success"
                  className="py-1 px-2"
                  style={{ fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4, backgroundColor: "#ECFDF5", borderColor: "#10B981", color: "#065F46" }}
                  onClick={handleExportFullExcel}
                  title="Export full member details, including vehicle, bank, expiry dates, and clickable photo links into Excel"
                >
                  <span>📊</span> Full Excel (.xls)
                </Button>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="py-1 px-2"
                  style={{ fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}
                  onClick={handleExportFullCSV}
                  title="Export all member columns in CSV format"
                >
                  <span>📥</span> CSV
                </Button>
                <input
                  type="text"
                  placeholder="Search Member..."
                  value={globalFilter || ""}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  className="form-control form-control-sm"
                  style={{ width: 180, height: 32, fontSize: 12 }}
                />
                <Button size="sm" variant="primary" className="py-1 px-3" style={{ fontSize: 12, fontWeight: 600 }} onClick={() => navigate("/AddMemberMaster", { state: { Id: 0 } })}>
                  + Add Member
                </Button>
              </div>
            </div>

            {/* Grid Table */}
            <div style={{ overflowX: "auto", width: "100%", marginBottom: 12 }}>
              <Table {...getTableProps()} size="sm" bordered hover responsive style={{ fontSize: 12, width: "100%", minWidth: "100%" }}>
                <thead>
                  {headerGroups.map((headerGroup, i) => (
                    <tr {...headerGroup.getHeaderGroupProps()} key={i}>
                      {headerGroup.headers.map((col, j) => (
                        <th
                          {...col.getHeaderProps(col.getSortByToggleProps())}
                          key={j}
                          style={{ padding: "8px 10px", fontSize: 11.5, fontWeight: 700, backgroundColor: "#E87B2D", color: "#fff", verticalAlign: "middle" }}
                        >
                          {col.render("Header")}
                          {col.isSorted && <span style={{ marginLeft: 3 }}>{col.isSortedDesc ? "▼" : "▲"}</span>}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row, i) => {
                    prepareRow(row);
                    const isDel = row.original.isDeleted;
                    return (
                      <tr
                        {...row.getRowProps()}
                        key={i}
                        style={{ backgroundColor: isDel ? "#FEF2F2" : "inherit" }}
                      >
                        {row.cells.map((cell, j) => (
                          <td {...cell.getCellProps()} key={j} style={{ padding: "6px 10px", verticalAlign: "middle" }}>
                            {cell.render("Cell")}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </div>

            {/* Pagination */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, flexWrap: "wrap", gap: 6, paddingTop: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span>Showing <strong>{pageIndex * pageSize + 1}</strong> to <strong>{Math.min((pageIndex + 1) * pageSize, data.length)}</strong> of <strong>{data.length}</strong> members</span>
                <span className="text-muted">|</span>
                <span>Page {pageIndex + 1} of {pageOptions.length || 1}</span>
                <select className="form-select form-select-sm" style={{ width: 65, height: 28, fontSize: 11 }} value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                  {[10, 15, 20, 30, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => gotoPage(0)} disabled={!canPreviousPage}>« First</button>
                <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => previousPage()} disabled={!canPreviousPage}>‹ Prev</button>
                <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => nextPage()} disabled={!canNextPage}>Next ›</button>
                <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" style={{ fontSize: 11 }} onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>Last »</button>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* DETAILED MEMBER DOSSIER MODAL */}
      <Modal show={viewModal.show} onHide={() => setViewModal({ show: false, member: null, walletInfo: null, loadingWallet: false })} size="lg" centered scrollable>
        <Modal.Header closeButton style={{ backgroundColor: "#1F2937", color: "#fff", padding: "10px 16px" }}>
          <Modal.Title style={{ fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 8 }}>
            <span>👤 Profile Dossier & Complete Registration Details</span>
            {selected && (
              <Badge bg={selected.isDeleted ? "danger" : "success"} style={{ fontSize: 11 }}>
                {selected.isDeleted ? "DELETED AUDIT LOG" : "ACTIVE MEMBER"}
              </Badge>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: "#F9FAFB", padding: 16 }}>
          {selected && (
            <>
              {/* DELETED AUDIT ALERT BANNER */}
              {selected.isDeleted && (
                <div style={{ backgroundColor: "#FEE2E2", border: "1px solid #F87171", borderRadius: 8, padding: 12, marginBottom: 15 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#991B1B", fontWeight: 700, fontSize: 13 }}>
                    <span>⚠️ PERMANENTLY DELETED RECORD AUDIT LOG</span>
                  </div>
                  <p style={{ margin: "4px 0 0 0", fontSize: 12, color: "#7F1D1D" }}>
                    This member profile and all associated wallet ledgers, vouchers, vehicles, and bookings were deleted on{" "}
                    <strong>{selected.deletedOn || "N/A"}</strong> by <strong>{selected.deletedBy || "Admin"}</strong>.
                  </p>
                </div>
              )}

              {/* PROFILE SUMMARY HEADER */}
              <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10, overflow: "hidden" }}>
                <div style={{ backgroundColor: isCaptain ? "#E87B2D" : "#3B82F6", height: 8 }} />
                <Card.Body className="p-3">
                  <Row className="align-items-center">
                    <Col md={3} className="text-center mb-2 mb-md-0">
                      {selected.UserPhoto ? (
                        <img
                          src={`${API_WEB_URLS.IMAGEURL}${selected.UserPhoto}`}
                          alt="Member Avatar"
                          style={{ width: 85, height: 85, borderRadius: "50%", objectFit: "cover", border: `3px solid ${isCaptain ? "#E87B2D" : "#3B82F6"}`, cursor: "pointer" }}
                          onClick={() => setPreviewImage(`${API_WEB_URLS.IMAGEURL}${selected.UserPhoto}`)}
                        />
                      ) : (
                        <div style={{ width: 85, height: 85, borderRadius: "50%", backgroundColor: "#E5E7EB", color: "#6B7280", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 700 }}>
                          {selected.Name ? selected.Name.charAt(0).toUpperCase() : "U"}
                        </div>
                      )}
                    </Col>
                    <Col md={9}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <h5 style={{ margin: 0, fontWeight: 700, color: "#111827" }}>{selected.Name || "N/A"}</h5>
                        <Badge bg={isCaptain ? "warning" : "info"} text={isCaptain ? "dark" : "white"}>
                          {isCaptain ? "🚖 Captain / Driver" : "👤 Customer / User"}
                        </Badge>
                        {selected.IsApproved === 1 && <Badge bg="success">Verified & Approved</Badge>}
                      </div>
                      <div style={{ fontSize: 12, color: "#4B5563", marginTop: 6, display: "flex", flexWrap: "wrap", gap: 14 }}>
                        <span>📱 <strong>Mobile:</strong> {selected.MobileNo || "-"}</span>
                        <span>✉️ <strong>Email:</strong> {selected.Email || "-"}</span>
                        <span>🆔 <strong>Member ID:</strong> #{selected.Id}</span>
                      </div>
                      <div style={{ fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                        📍 <strong>Full Address:</strong> {selected.Address || "-"}, {selected.City || selected.CityName || ""}, {selected.StateName || ""}, Pincode: {selected.Pincode || "-"}
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* CAPTAIN WALLET & EARNINGS BREAKDOWN (FROM WALLETBALANCE API) */}
              {isCaptain && !selected.isDeleted && (
                <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10, borderLeft: "4px solid #F59E0B" }}>
                  <Card.Header style={{ backgroundColor: "#FEF3C7", fontWeight: 700, fontSize: 13, color: "#92400E", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span>💰 Live Captain Wallet & Earnings Breakdown</span>
                    {viewModal.loadingWallet && <Spinner animation="border" size="sm" />}
                  </Card.Header>
                  <Card.Body className="p-3" style={{ fontSize: 12 }}>
                    {walletInfo ? (
                      <Row className="g-3 text-center">
                        <Col md={3}>
                          <div style={{ backgroundColor: "#ECFDF5", borderRadius: 8, padding: 8, border: "1px solid #10B981" }}>
                            <small style={{ color: "#065F46", fontWeight: 600 }}>Wallet Balance</small>
                            <h6 style={{ margin: 0, fontWeight: 700, color: "#047857" }}>₹ {walletInfo.WalletBalance || 0}</h6>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div style={{ backgroundColor: "#EFF6FF", borderRadius: 8, padding: 8, border: "1px solid #3B82F6" }}>
                            <small style={{ color: "#1E40AF", fontWeight: 600 }}>Total Collection</small>
                            <h6 style={{ margin: 0, fontWeight: 700, color: "#1D4ED8" }}>₹ {walletInfo.TotalCollection || 0}</h6>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div style={{ backgroundColor: "#F5F3FF", borderRadius: 8, padding: 8, border: "1px solid #8B5CF6" }}>
                            <small style={{ color: "#5B21B6", fontWeight: 600 }}>Online Collection</small>
                            <h6 style={{ margin: 0, fontWeight: 700, color: "#6D28D9" }}>₹ {walletInfo.OnlineCollection || 0}</h6>
                          </div>
                        </Col>
                        <Col md={3}>
                          <div style={{ backgroundColor: "#FEF2F2", borderRadius: 8, padding: 8, border: "1px solid #EF4444" }}>
                            <small style={{ color: "#991B1B", fontWeight: 600 }}>Cash Collection</small>
                            <h6 style={{ margin: 0, fontWeight: 700, color: "#B91C1C" }}>₹ {walletInfo.CashCollection || 0}</h6>
                          </div>
                        </Col>
                      </Row>
                    ) : (
                      <span className="text-muted" style={{ fontSize: 11 }}>Wallet & earnings summary loading or unavailable.</span>
                    )}
                  </Card.Body>
                </Card>
              )}

              {/* PERSONAL & IDENTITY INFORMATION */}
              <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10 }}>
                <Card.Header style={{ backgroundColor: "#F3F4F6", fontWeight: 700, fontSize: 13, color: "#374151" }}>
                  🪪 Personal & Identity Verification
                </Card.Header>
                <Card.Body className="p-3" style={{ fontSize: 12 }}>
                  <Row className="g-3">
                    <Col md={4}><strong>Father Name:</strong> {selected.FatherName || "-"}</Col>
                    <Col md={4}><strong>Mother Name:</strong> {selected.MotherName || "-"}</Col>
                    <Col md={4}><strong>Date of Birth / Age:</strong> {selected.DateOfBirth || "-"} ({selected.Age || "-"} yrs)</Col>
                    <Col md={4}><strong>PAN Number:</strong> <Badge bg="secondary">{selected.PANNo || selected.PanNo || "-"}</Badge></Col>
                    <Col md={4}><strong>Aadhaar Number:</strong> <Badge bg="secondary">{selected.AadharNo || "-"}</Badge></Col>
                    <Col md={4}><strong>Driving License No:</strong> <Badge bg="secondary">{selected.DrivingLicence || "-"}</Badge></Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* USER / BUSINESS DETAILS (IF CUSTOMER) */}
              {!isCaptain && (
                <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10, borderLeft: "4px solid #6366F1" }}>
                  <Card.Header style={{ backgroundColor: "#EEF2FF", fontWeight: 700, fontSize: 13, color: "#3730A3" }}>
                    🏢 Customer & Business Profile Classification
                  </Card.Header>
                  <Card.Body className="p-3" style={{ fontSize: 12 }}>
                    <Row className="g-3">
                      <Col md={4}><strong>Account Type:</strong> {selected.F_MemberCategory === 1 ? "Business / Company Account" : "Individual Customer"}</Col>
                      <Col md={4}><strong>Company Name:</strong> {selected.CompanyName || "-"}</Col>
                      <Col md={4}><strong>GST Number:</strong> <Badge bg="info">{selected.GSTNo || "-"}</Badge></Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* BANK ACCOUNT DETAILS */}
              <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10, borderLeft: "4px solid #10B981" }}>
                <Card.Header style={{ backgroundColor: "#ECFDF5", fontWeight: 700, fontSize: 13, color: "#065F46" }}>
                  🏦 Bank Account Details (Payouts & Transfers)
                </Card.Header>
                <Card.Body className="p-3" style={{ fontSize: 12 }}>
                  <Row className="g-3">
                    <Col md={6}><strong>Bank Name:</strong> {selected.BankName || "-"}</Col>
                    <Col md={6}><strong>Account Number:</strong> {selected.AccountNo || "-"}</Col>
                    <Col md={6}><strong>IFSC Code:</strong> {selected.IFSC || "-"}</Col>
                    <Col md={6}><strong>Account Holder Name:</strong> {selected.AccountPersonName || selected.Name || "-"}</Col>
                  </Row>
                </Card.Body>
              </Card>

              {/* VEHICLE DETAILS (FROM VEHICLEMASTER FOR CAPTAINS) */}
              {isCaptain && (
                <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10, borderLeft: "4px solid #3B82F6" }}>
                  <Card.Header style={{ backgroundColor: "#EFF6FF", fontWeight: 700, fontSize: 13, color: "#1E40AF" }}>
                    🚛 Registered Vehicle Information (VehicleMaster)
                  </Card.Header>
                  <Card.Body className="p-3" style={{ fontSize: 12 }}>
                    <Row className="g-3">
                      <Col md={6}><strong>Vehicle Name:</strong> {selected.VehicleName || "-"}</Col>
                      <Col md={6}><strong>Chassis / Registration No:</strong> <Badge bg="primary">{selected.VehicleRegistrationNo || "-"}</Badge></Col>
                      <Col md={6}><strong>Plate Vehicle No:</strong> <Badge bg="dark">{selected.VehicleNo || "-"}</Badge></Col>
                      <Col md={6}><strong>Vehicle Type / Category:</strong> <Badge bg="info">{getVehicleTypeName(selected)}</Badge></Col>
                      <Col md={6}><strong>Weight Capacity:</strong> {selected.WeightCapacity || "-"} kg</Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* DOCUMENT EXPIRY DATES */}
              {(selected.DLExpiryDate || selected.InsuranceExpiryDate || selected.PUCExpiryDate) && (
                <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10, borderLeft: "4px solid #EF4444" }}>
                  <Card.Header style={{ backgroundColor: "#FEF2F2", fontWeight: 700, fontSize: 13, color: "#991B1B" }}>
                    📅 Official Document Expiry Dates & Validity
                  </Card.Header>
                  <Card.Body className="p-3" style={{ fontSize: 12 }}>
                    <Row className="g-3">
                      <Col md={4}>
                        <div><strong>Driving License Expiry:</strong></div>
                        <div className="mt-1">
                          <span className="me-2">{formatDate(selected.DLExpiryDate)}</span>
                          <Badge bg={getExpiryStatus(selected.DLExpiryDate) === "EXPIRED" ? "danger" : "success"}>
                            {getExpiryStatus(selected.DLExpiryDate)}
                          </Badge>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div><strong>Insurance Expiry:</strong></div>
                        <div className="mt-1">
                          <span className="me-2">{formatDate(selected.InsuranceExpiryDate)}</span>
                          <Badge bg={getExpiryStatus(selected.InsuranceExpiryDate) === "EXPIRED" ? "danger" : "success"}>
                            {getExpiryStatus(selected.InsuranceExpiryDate)}
                          </Badge>
                        </div>
                      </Col>
                      <Col md={4}>
                        <div><strong>PUC Expiry:</strong></div>
                        <div className="mt-1">
                          <span className="me-2">{formatDate(selected.PUCExpiryDate)}</span>
                          <Badge bg={getExpiryStatus(selected.PUCExpiryDate) === "EXPIRED" ? "danger" : "success"}>
                            {getExpiryStatus(selected.PUCExpiryDate)}
                          </Badge>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              )}

              {/* DOCUMENT & ATTACHMENTS GALLERY */}
              {docList.length > 0 && (
                <Card className="shadow-sm border-0 mb-3" style={{ borderRadius: 10 }}>
                  <Card.Header style={{ backgroundColor: "#F3F4F6", fontWeight: 700, fontSize: 13, color: "#374151" }}>
                    📷 Uploaded Verification Documents ({docList.length})
                  </Card.Header>
                  <Card.Body className="p-3">
                    <Row className="g-3">
                      {docList.map((doc, idx) => {
                        const fullUrl = `${API_WEB_URLS.IMAGEURL}${doc.url}`;
                        return (
                          <Col key={idx} md={4} sm={6} className="text-center">
                            <div style={{ border: "1px solid #E5E7EB", borderRadius: 8, padding: 8, backgroundColor: "#fff" }}>
                              <span style={{ fontSize: 11, fontWeight: 600, color: "#4B5563", display: "block", marginBottom: 6 }}>
                                {doc.title}
                              </span>
                              <img
                                src={fullUrl}
                                alt={doc.title}
                                style={{ width: "100%", height: 110, objectFit: "cover", borderRadius: 6, cursor: "pointer" }}
                                onClick={() => setPreviewImage(fullUrl)}
                              />
                            </div>
                          </Col>
                        );
                      })}
                    </Row>
                  </Card.Body>
                </Card>
              )}
            </>
          )}
        </Modal.Body>
        <Modal.Footer style={{ backgroundColor: "#F3F4F6", padding: "8px 16px" }}>
          <Button size="sm" variant="secondary" onClick={() => setViewModal({ show: false, member: null, walletInfo: null, loadingWallet: false })}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* FULLSCREEN LIGHTBOX IMAGE PREVIEW MODAL */}
      <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: "#111827", color: "#fff" }}>
          <Modal.Title style={{ fontSize: 14 }}>Document Preview</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ backgroundColor: "#000", textAlign: "center", padding: 10 }}>
          {previewImage && (
            <img src={previewImage} alt="Document Preview" style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }} />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default PageList_MemberMaster;
